import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent } from '../../components/ui/tabs';
import { IvrNav } from '../../components/IvrNav';
import { Plus, Edit, Trash2, Upload, Play, X, ChevronUp, ChevronDown } from 'lucide-react';

import { API_URL, BACKEND_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

const TYPING_SYSTEMS = [
  { value: 'yiddish_keypad', label: 'Yiddish Keypad' },
  { value: 'english_keypad', label: 'English Keypad' },
  { value: 'address_keypad', label: 'Address Keypad' },
  { value: 'email_keypad', label: 'Email Keypad' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'number', label: 'Number (Predefined Options)' },
  { value: 'single_select', label: 'Single Select (Options with Audio/TTS)' },
  { value: 'digits', label: 'Digits' },
  { value: 'digits_4', label: '4 Digits' },
];

/** The settings pages of IVR Management, each at /admin/ivr/<page> (the audio pages are AdminIvrAudio). */
export const IVR_SETTINGS_PAGES = ['field-inputs', 'category-assignment', 'menu-messages', 'character-recordings', 'simcha-week'];

export default function AdminIVR({ section = 'field-inputs' }) {
  const { token } = useAuth();
  const [fieldInputs, setFieldInputs] = useState([]);
  const [fieldKeys, setFieldKeys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryFieldInputs, setCategoryFieldInputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFieldInput, setEditingFieldInput] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  
  // Menu Messages state
  const [menuMessages, setMenuMessages] = useState([]);
  const [editingMenuMessage, setEditingMenuMessage] = useState(null);
  const [isMenuMessageDialogOpen, setIsMenuMessageDialogOpen] = useState(false);
  const [menuMessageAudioFile, setMenuMessageAudioFile] = useState(null);
  
  // Character Recordings state
  const [characterRecordings, setCharacterRecordings] = useState([]);
  const [editingCharacterRecording, setEditingCharacterRecording] = useState(null);
  const [isCharacterDialogOpen, setIsCharacterDialogOpen] = useState(false);
  const [characterAudioFile, setCharacterAudioFile] = useState(null);

  // Simcha Week Calendar state
  const [simchaWeekCalendar, setSimchaWeekCalendar] = useState([]);
  const [editingSimchaWeek, setEditingSimchaWeek] = useState(null);
  const [isSimchaWeekDialogOpen, setIsSimchaWeekDialogOpen] = useState(false);
  const [simchaWeekAudioFile, setSimchaWeekAudioFile] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadCategoryFieldInputs(selectedCategory);
    }
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [fieldInputsRes, categoriesRes, messagesRes, recordingsRes, fieldKeysRes, simchaCalendarRes] = await Promise.all([
        axios.get(`${API_URL}/admin/ivr/field-inputs`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/admin/ivr/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/ivr/character-recordings`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/ivr/field-keys`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/ivr/simcha-week-calendar`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);
      setFieldInputs(fieldInputsRes.data);
      setCategories(categoriesRes.data);
      setMenuMessages(messagesRes.data);
      setCharacterRecordings(recordingsRes.data);
      setFieldKeys(fieldKeysRes.data);
      setSimchaWeekCalendar(simchaCalendarRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load data'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Targeted reload functions — only fetch what changed, no loading spinner
  const reloadCharacterRecordings = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/ivr/character-recordings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCharacterRecordings(res.data);
    } catch (error) {
      console.error('Failed to reload character recordings', error);
    }
  };

  const reloadSimchaWeekCalendar = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/ivr/simcha-week-calendar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSimchaWeekCalendar(res.data);
    } catch (error) {
      console.error('Failed to reload simcha week calendar', error);
    }
  };

  const reloadMenuMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/ivr/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenuMessages(res.data);
    } catch (error) {
      console.error('Failed to reload menu messages', error);
    }
  };

  const reloadFieldInputs = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/ivr/field-inputs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFieldInputs(res.data);
    } catch (error) {
      console.error('Failed to reload field inputs', error);
    }
  };

  const loadCategoryFieldInputs = async (categoryId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/categories/${categoryId}/field-inputs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategoryFieldInputs(res.data);
    } catch (error) {
      console.error(error);
      setCategoryFieldInputs([]);
    }
  };

  const handleCreateFieldInput = () => {
    setEditingFieldInput({
      name: '',
      field_key: 'custom',
      typing_system: 'english_keypad',
      prompt_text: '',
      prompt_audio_url: '',
      number_amount: null,
      number_options: [],
      selection_options: [],
      fallback_typing_system: null,
      fallback_typing_prompt: '',
      fallback_typing_audio_url: '',
    });
    setAudioFile(null);
    setAudioPreview(null);
    setIsDialogOpen(true);
  };

  const handleEditFieldInput = (fieldInput) => {
    const fi = { ...fieldInput };
    // Migrate legacy number_options to selection_options if needed
    if ((fi.typing_system === 'number' || fi.typing_system === 'single_select') && (!fi.selection_options || fi.selection_options.length === 0)) {
      if (fi.number_options?.length > 0) {
        fi.selection_options = fi.number_options.map(text => ({ text, audio_url: '', tts_text: '' }));
      } else if (fi.select_options?.length > 0) {
        // Migrate from old single_select select_options field
        fi.selection_options = fi.select_options.map(opt => ({
          text: opt.text || '',
          audio_url: opt.audio_url || '',
          tts_text: opt.tts_text || '',
          continue_typing: false,
        }));
      }
    }
    if (!fi.selection_options) fi.selection_options = [];
    if (!fi.fallback_typing_system) fi.fallback_typing_system = null;
    if (!fi.fallback_typing_prompt) fi.fallback_typing_prompt = '';
    if (!fi.fallback_typing_audio_url) fi.fallback_typing_audio_url = '';
    setEditingFieldInput(fi);
    setAudioFile(null);
    setAudioPreview(fieldInput.prompt_audio_url ? `${BACKEND_URL}${fieldInput.prompt_audio_url}` : null);
    setIsDialogOpen(true);
  };

  const handleDeleteFieldInput = async (id) => {
    try {
      await axios.delete(`${API_URL}/admin/ivr/field-inputs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Field input deleted');
      reloadFieldInputs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete field input'));
      console.error(error);
    }
  };

  const handleAudioUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/upload/audio`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data.url;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload audio'));
      throw error;
    }
  };

  const handleSaveFieldInput = async () => {
    try {
      let audioUrl = editingFieldInput.prompt_audio_url;
      
      if (audioFile) {
        audioUrl = await handleAudioUpload(audioFile);
      }

      const data = {
        ...editingFieldInput,
        prompt_audio_url: audioUrl,
      };

      if (editingFieldInput.id) {
        await axios.put(`${API_URL}/admin/ivr/field-inputs/${editingFieldInput.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Field input updated');
      } else {
        await axios.post(`${API_URL}/admin/ivr/field-inputs`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Field input created');
      }

      setIsDialogOpen(false);
      setEditingFieldInput(null);
      setAudioFile(null);
      setAudioPreview(null);
      reloadFieldInputs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save field input'));
      console.error(error);
    }
  };

  const handleAssignFieldInputs = async () => {
    if (!selectedCategory) {
      toast.error('Please select a category first');
      return;
    }

    try {
      const assignments = categoryFieldInputs.map((fi, index) => ({
        field_input_id: fi.id,
        order: index + 1,
      }));

      await axios.put(
        `${API_URL}/admin/categories/${selectedCategory}/field-inputs`,
        assignments,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Field inputs assigned to category');
      loadCategoryFieldInputs(selectedCategory);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign field inputs'));
      console.error(error);
    }
  };

  const handleAddFieldInputToCategory = (fieldInputId) => {
    const fieldInput = fieldInputs.find((fi) => fi.id === fieldInputId);
    if (fieldInput && !categoryFieldInputs.find((fi) => fi.id === fieldInputId)) {
      setCategoryFieldInputs([...categoryFieldInputs, fieldInput]);
    }
  };

  const handleRemoveFieldInputFromCategory = (fieldInputId) => {
    setCategoryFieldInputs(categoryFieldInputs.filter((fi) => fi.id !== fieldInputId));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const items = [...categoryFieldInputs];
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    setCategoryFieldInputs(items);
  };

  const handleMoveDown = (index) => {
    if (index === categoryFieldInputs.length - 1) return;
    const items = [...categoryFieldInputs];
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    setCategoryFieldInputs(items);
  };

  const playAudio = (url) => {
    const audio = new Audio(url);
    audio.play();
  };

  const handleSaveSimchaWeek = async () => {
    try {
      if (!editingSimchaWeek) return;
      let audioUrl = editingSimchaWeek.audio_url || '';
      // Upload audio file if provided
      if (simchaWeekAudioFile) {
        const formData = new FormData();
        formData.append('file', simchaWeekAudioFile);
        const uploadRes = await axios.post(`${API_URL}/upload/audio`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        audioUrl = uploadRes.data.url || uploadRes.data.file_url || '';
      }
      const payload = {
        parsha_en: editingSimchaWeek.parsha_en,
        parsha_he: editingSimchaWeek.parsha_he,
        yiddish_name: editingSimchaWeek.yiddish_name || '',
        tts_text: editingSimchaWeek.tts_text || editingSimchaWeek.parsha_en || '',
        audio_url: audioUrl,
      };
      if (editingSimchaWeek.recording_id) {
        await axios.put(`${API_URL}/admin/ivr/simcha-week-recordings/${editingSimchaWeek.recording_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/admin/ivr/simcha-week-recordings`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      toast.success('Simcha week recording saved');
      setIsSimchaWeekDialogOpen(false);
      setEditingSimchaWeek(null);
      setSimchaWeekAudioFile(null);
      reloadSimchaWeekCalendar();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save simcha week recording'));
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-paper">
        <AdminSidebar />
        <div className="ml-64 flex-1 min-w-0 p-4 sm:p-8">
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">IVR Management</h1>
          <p className="text-ink/60 mb-6">Phone-line prompts, field inputs and recordings</p>
          <IvrNav />
          <div className="flex justify-center py-20" role="status">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-gold/20 border-t-gold" />
            <span className="sr-only">Loading IVR settings</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 flex-1 min-w-0 p-4 sm:p-8">
        <h1 className="font-serif text-3xl font-bold text-ink mb-2">IVR Management</h1>
        <p className="text-ink/60 mb-6">Phone-line prompts, field inputs and recordings</p>
        <IvrNav />

        <Tabs value={section} className="space-y-6">

          <TabsContent value="field-inputs" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold">Field Inputs</h2>
              <Button onClick={handleCreateFieldInput}>
                <Plus className="h-4 w-4 mr-2" />
                Create Field Input
              </Button>
            </div>

            <div className="bg-white rounded-lg shadow">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Field Key</TableHead>
                    <TableHead>Typing System</TableHead>
                    <TableHead>Audio</TableHead>
                    <TableHead>Text (TTS)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fieldInputs.map((fieldInput) => (
                    <TableRow key={fieldInput.id}>
                      <TableCell className="font-medium">{fieldInput.name}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${
                          fieldInput.field_key === 'custom' ? 'bg-gray-100 text-gray-600' :
                          fieldInput.field_key === 'simcha_week' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {fieldKeys.find((fk) => fk.key === fieldInput.field_key)?.label ||
                            fieldInput.field_key || 'custom'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          {TYPING_SYSTEMS.find((ts) => ts.value === fieldInput.typing_system)?.label ||
                            fieldInput.typing_system}
                          {(fieldInput.typing_system === 'number' || fieldInput.typing_system === 'single_select') && fieldInput.selection_options?.length > 0 && (
                            <span className="text-xs text-gray-500 block">
                              {fieldInput.selection_options.length} option{fieldInput.selection_options.length !== 1 ? 's' : ''}
                              {fieldInput.fallback_typing_system && ' (+ type your own)'}
                              {fieldInput.selection_options.some(o => o.continue_typing) && ' (+ continue typing)'}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {fieldInput.prompt_audio_url ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              playAudio(`${BACKEND_URL}${fieldInput.prompt_audio_url}`)
                            }
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-gray-400">No audio</span>
                        )}
                      </TableCell>
                      <TableCell>{fieldInput.prompt_text || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditFieldInput(fieldInput)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Field Input</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{fieldInput.name}"? This action
                                  cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteFieldInput(fieldInput.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="category-assignment" className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label>Select Category</Label>
                <Select value={selectedCategory || ''} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCategory && (
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Available Field Inputs</h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {fieldInputs
                        .filter((fi) => !categoryFieldInputs.find((cfi) => cfi.id === fi.id))
                        .map((fieldInput) => (
                          <div
                            key={fieldInput.id}
                            className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                          >
                            <span>{fieldInput.name}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddFieldInputToCategory(fieldInput.id)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Assigned Field Inputs (Order)</h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {categoryFieldInputs.map((fieldInput, index) => (
                        <div
                          key={fieldInput.id}
                          className="flex items-center gap-2 p-3 border rounded bg-gray-50"
                        >
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-4 w-4 p-0"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-4 w-4 p-0"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === categoryFieldInputs.length - 1}
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>
                          <span className="flex-1">{fieldInput.name}</span>
                          <span className="text-sm text-gray-500">#{index + 1}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveFieldInputFromCategory(fieldInput.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      className="mt-4 w-full"
                      onClick={handleAssignFieldInputs}
                      disabled={categoryFieldInputs.length === 0}
                    >
                      Save Assignment
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="menu-messages" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold">Menu Messages</h2>
              <Button onClick={() => {
                setEditingMenuMessage({
                  message_key: '',
                  message_name: '',
                  audio_url: '',
                  text: '',
                  audio_link: '',
                });
                setMenuMessageAudioFile(null);
                setIsMenuMessageDialogOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Menu Message
              </Button>
            </div>

            <div className="bg-white rounded-lg shadow">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message Key</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Audio</TableHead>
                    <TableHead>Text (TTS)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {menuMessages.map((message) => (
                    <TableRow key={message.id}>
                      <TableCell className="font-medium">{message.message_key}</TableCell>
                      <TableCell>{message.message_name}</TableCell>
                      <TableCell>
                        {message.audio_url || message.audio_link ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => playAudio(
                              message.audio_url 
                                ? `${BACKEND_URL}${message.audio_url}`
                                : message.audio_link
                            )}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-gray-400">No audio</span>
                        )}
                      </TableCell>
                      <TableCell>{message.text || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingMenuMessage({ ...message });
                              setMenuMessageAudioFile(null);
                              setIsMenuMessageDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Menu Message</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{message.message_name}"?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={async () => {
                                    try {
                                      await axios.delete(`${API_URL}/admin/ivr/messages/${message.id}`, {
                                        headers: { Authorization: `Bearer ${token}` },
                                      });
                                      toast.success('Menu message deleted');
                                      reloadMenuMessages();
                                    } catch (error) {
                                      toast.error(getErrorMessage(error, 'Failed to delete menu message'));
                                    }
                                  }}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="character-recordings" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold">Character Recordings</h2>
              <Button onClick={() => {
                setEditingCharacterRecording({
                  character: '',
                  audio_url: '',
                  text: '',
                  audio_link: '',
                });
                setCharacterAudioFile(null);
                setIsCharacterDialogOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Character Recording
              </Button>
            </div>

            <div className="bg-white rounded-lg shadow">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Character</TableHead>
                    <TableHead>Audio</TableHead>
                    <TableHead>Text (TTS)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {characterRecordings.map((recording) => (
                    <TableRow key={recording.id}>
                      <TableCell className="font-medium text-2xl">{recording.character}</TableCell>
                      <TableCell>
                        {recording.audio_url || recording.audio_link ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => playAudio(
                              recording.audio_url 
                                ? `${BACKEND_URL}${recording.audio_url}`
                                : recording.audio_link
                            )}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-gray-400">No audio</span>
                        )}
                      </TableCell>
                      <TableCell>{recording.text || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingCharacterRecording({ ...recording });
                              setCharacterAudioFile(null);
                              setIsCharacterDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Character Recording</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete the recording for "{recording.character}"?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={async () => {
                                    try {
                                      await axios.delete(`${API_URL}/admin/ivr/character-recordings/${recording.id}`, {
                                        headers: { Authorization: `Bearer ${token}` },
                                      });
                                      toast.success('Character recording deleted');
                                      reloadCharacterRecordings();
                                    } catch (error) {
                                      toast.error(getErrorMessage(error, 'Failed to delete character recording'));
                                    }
                                  }}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="simcha-week" className="space-y-6">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-semibold">Simcha Week Calendar</h2>
              <p className="text-gray-500 text-sm mt-1 mb-4">Set Yiddish names and audio recordings for each weekly parsha. These are played in the IVR when callers select the simcha week field.</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Parsha (English)</TableHead>
                    <TableHead>Parsha (Hebrew)</TableHead>
                    <TableHead>Yiddish Name</TableHead>
                    <TableHead>Audio</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {simchaWeekCalendar.map((week) => (
                    <TableRow key={week.date} className={week.has_recording ? 'bg-green-50' : ''}>
                      <TableCell>{week.date}</TableCell>
                      <TableCell>{week.parsha_en}</TableCell>
                      <TableCell className="text-right font-hebrew">{week.parsha_he}</TableCell>
                      <TableCell className="text-right font-hebrew">{week.yiddish_name || <span className="text-gray-400 italic">Not set</span>}</TableCell>
                      <TableCell>
                        {week.audio_url ? (
                          <audio controls src={week.audio_url} className="h-8 w-32" />
                        ) : (
                          <span className="text-gray-400 text-sm">No audio</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingSimchaWeek(week);
                            setSimchaWeekAudioFile(null);
                            setIsSimchaWeekDialogOpen(true);
                          }}
                        >
                          {week.has_recording ? 'Edit' : 'Set'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {simchaWeekCalendar.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-8">Loading calendar data...</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        {/* Simcha Week Recording Dialog */}
        <Dialog open={isSimchaWeekDialogOpen} onOpenChange={setIsSimchaWeekDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingSimchaWeek?.has_recording ? 'Edit' : 'Set'} Simcha Week Recording
              </DialogTitle>
            </DialogHeader>
            {editingSimchaWeek && (
              <div className="space-y-4 py-4">
                <div>
                  <Label>Parsha</Label>
                  <p className="font-medium">{editingSimchaWeek.parsha_en} - {editingSimchaWeek.parsha_he} ({editingSimchaWeek.date})</p>
                </div>
                <div>
                  <Label>Yiddish Name</Label>
                  <Input
                    dir="rtl"
                    value={editingSimchaWeek.yiddish_name || ''}
                    onChange={(e) => setEditingSimchaWeek({ ...editingSimchaWeek, yiddish_name: e.target.value })}
                    placeholder="Enter Yiddish parsha name"
                  />
                </div>
                <div>
                  <Label>TTS Text (English, used as fallback when no audio)</Label>
                  <Input
                    value={editingSimchaWeek.tts_text || ''}
                    onChange={(e) => setEditingSimchaWeek({ ...editingSimchaWeek, tts_text: e.target.value })}
                    placeholder={editingSimchaWeek.parsha_en || 'e.g. Vayakhel-Pekudei'}
                  />
                  <p className="text-xs text-gray-500 mt-1">The IVR will say this name in English TTS when no audio recording is set.</p>
                </div>
                <div>
                  <Label>Audio Recording (overrides TTS)</Label>
                  {editingSimchaWeek.audio_url && (
                    <audio controls src={editingSimchaWeek.audio_url} className="w-full mb-2" />
                  )}
                  <Input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => setSimchaWeekAudioFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsSimchaWeekDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleSaveSimchaWeek}>Save</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Field Input Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingFieldInput?.id ? 'Edit Field Input' : 'Create Field Input'}
              </DialogTitle>
            </DialogHeader>
            {editingFieldInput && (
              <div className="space-y-4">
                <div>
                  <Label>Field Key (what this field represents)</Label>
                  <Select
                    value={editingFieldInput.field_key || 'custom'}
                    onValueChange={(value) => {
                      const fk = fieldKeys.find(k => k.key === value);
                      setEditingFieldInput({
                        ...editingFieldInput,
                        field_key: value,
                        name: editingFieldInput.name || (fk && value !== 'custom' ? fk.label : ''),
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fieldKeys.map((fk) => (
                        <SelectItem key={fk.key} value={fk.key}>
                          {fk.label}
                          {fk.save_to_profile && <span className="text-green-600 ml-1">(saved to profile)</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {editingFieldInput.field_key === 'simcha_week'
                      ? 'Auto-populates from Hebrew calendar. IVR asks: this week (1), next week (2), or type own (3).'
                      : editingFieldInput.field_key !== 'custom' && fieldKeys.find(k => k.key === editingFieldInput.field_key)?.save_to_profile
                      ? 'This value will be saved to the customer profile and reused on future IVR orders.'
                      : 'Custom field — admin-defined name, not saved to customer profile.'}
                  </p>
                </div>

                <div>
                  <Label>Name</Label>
                  <Input
                    value={editingFieldInput.name}
                    onChange={(e) =>
                      setEditingFieldInput({ ...editingFieldInput, name: e.target.value })
                    }
                    placeholder="e.g., Enter your name"
                  />
                </div>

                <div>
                  <Label>Typing System</Label>
                  <Select
                    value={editingFieldInput.typing_system}
                    onValueChange={(value) =>
                      setEditingFieldInput({ ...editingFieldInput, typing_system: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPING_SYSTEMS.map((ts) => (
                        <SelectItem key={ts.value} value={ts.value}>
                          {ts.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Audio File (Optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setAudioFile(file);
                          setAudioPreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                    {audioPreview && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => playAudio(audioPreview)}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                    )}
                  </div>
                  {editingFieldInput.prompt_audio_url && !audioFile && (
                    <p className="text-sm text-gray-500 mt-1">
                      Current: {editingFieldInput.prompt_audio_url}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Text for TTS (Fallback if no audio)</Label>
                  <Input
                    value={editingFieldInput.prompt_text}
                    onChange={(e) =>
                      setEditingFieldInput({ ...editingFieldInput, prompt_text: e.target.value })
                    }
                    placeholder="e.g., Please enter your name"
                  />
                </div>

                {(editingFieldInput.typing_system === 'number' || editingFieldInput.typing_system === 'single_select') && (
                  <>
                    <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
                      <div className="flex justify-between items-center">
                        <Label className="text-base font-semibold">Selection Options</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const opts = [...(editingFieldInput.selection_options || [])];
                            opts.push({ text: '', audio_url: '', tts_text: '' });
                            setEditingFieldInput({
                              ...editingFieldInput,
                              selection_options: opts,
                              number_amount: opts.length,
                              number_options: opts.map(o => o.text),
                            });
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Option
                        </Button>
                      </div>

                      {(editingFieldInput.selection_options || []).length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">No options yet. Click "Add Option" to create selection choices.</p>
                      )}

                      {(editingFieldInput.selection_options || []).map((opt, idx) => (
                        <div key={idx} className="bg-white border rounded p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-600">Option {idx + 1} (press {idx + 1})</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const opts = [...(editingFieldInput.selection_options || [])];
                                opts.splice(idx, 1);
                                setEditingFieldInput({
                                  ...editingFieldInput,
                                  selection_options: opts,
                                  number_amount: opts.length,
                                  number_options: opts.map(o => o.text),
                                });
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </Button>
                          </div>
                          <div>
                            <Label className="text-xs">Text / Value</Label>
                            <Input
                              value={opt.text || ''}
                              onChange={(e) => {
                                const opts = [...(editingFieldInput.selection_options || [])];
                                opts[idx] = { ...opts[idx], text: e.target.value };
                                setEditingFieldInput({
                                  ...editingFieldInput,
                                  selection_options: opts,
                                  number_options: opts.map(o => o.text),
                                });
                              }}
                              placeholder="e.g., Wedding, Bar Mitzvah"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">TTS Text (what the IVR reads aloud)</Label>
                            <Input
                              value={opt.tts_text || ''}
                              onChange={(e) => {
                                const opts = [...(editingFieldInput.selection_options || [])];
                                opts[idx] = { ...opts[idx], tts_text: e.target.value };
                                setEditingFieldInput({ ...editingFieldInput, selection_options: opts });
                              }}
                              placeholder={`e.g., For ${opt.text || 'this option'} press ${idx + 1}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Audio Recording (optional)</Label>
                            <div className="flex gap-2">
                              <Input
                                type="file"
                                accept="audio/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    try {
                                      const url = await handleAudioUpload(file);
                                      const opts = [...(editingFieldInput.selection_options || [])];
                                      opts[idx] = { ...opts[idx], audio_url: url };
                                      setEditingFieldInput({ ...editingFieldInput, selection_options: opts });
                                      toast.success(`Audio uploaded for option ${idx + 1}`);
                                    } catch (err) { /* handled in handleAudioUpload */ }
                                  }
                                }}
                              />
                              {opt.audio_url && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => playAudio(`${BACKEND_URL}${opt.audio_url}`)}
                                >
                                  <Play className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Continue typing after selection */}
                          <div className="border-t pt-2 mt-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`continue-typing-${idx}`}
                                checked={!!opt.continue_typing}
                                onChange={(e) => {
                                  const opts = [...(editingFieldInput.selection_options || [])];
                                  opts[idx] = {
                                    ...opts[idx],
                                    continue_typing: e.target.checked,
                                    continue_typing_system: e.target.checked ? (opts[idx].continue_typing_system || 'yiddish_keypad') : null,
                                  };
                                  setEditingFieldInput({ ...editingFieldInput, selection_options: opts });
                                }}
                                className="rounded"
                              />
                              <Label htmlFor={`continue-typing-${idx}`} className="text-xs cursor-pointer">
                                Continue typing after selection (append text)
                              </Label>
                            </div>
                            {opt.continue_typing && (
                              <div className="ml-5 mt-2 space-y-2 bg-purple-50/50 rounded p-2">
                                <p className="text-xs text-gray-500">
                                  After choosing "{opt.text || `option ${idx+1}`}", the caller types additional text that gets appended.
                                </p>
                                <div>
                                  <Label className="text-xs">Typing System</Label>
                                  <Select
                                    value={opt.continue_typing_system || 'yiddish_keypad'}
                                    onValueChange={(value) => {
                                      const opts = [...(editingFieldInput.selection_options || [])];
                                      opts[idx] = { ...opts[idx], continue_typing_system: value };
                                      setEditingFieldInput({ ...editingFieldInput, selection_options: opts });
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {TYPING_SYSTEMS.filter(ts => ts.value !== 'number').map((ts) => (
                                        <SelectItem key={ts.value} value={ts.value}>
                                          {ts.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">TTS Prompt (e.g. "Now type the shul name")</Label>
                                  <Input
                                    value={opt.continue_typing_prompt || ''}
                                    onChange={(e) => {
                                      const opts = [...(editingFieldInput.selection_options || [])];
                                      opts[idx] = { ...opts[idx], continue_typing_prompt: e.target.value };
                                      setEditingFieldInput({ ...editingFieldInput, selection_options: opts });
                                    }}
                                    placeholder="e.g., Now type the name"
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Audio Prompt (optional)</Label>
                                  <div className="flex gap-2">
                                    <Input
                                      type="file"
                                      accept="audio/*"
                                      className="h-8"
                                      onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          try {
                                            const url = await handleAudioUpload(file);
                                            const opts = [...(editingFieldInput.selection_options || [])];
                                            opts[idx] = { ...opts[idx], continue_typing_audio_url: url };
                                            setEditingFieldInput({ ...editingFieldInput, selection_options: opts });
                                            toast.success('Continue-typing audio uploaded');
                                          } catch (err) { /* handled */ }
                                        }
                                      }}
                                    />
                                    {opt.continue_typing_audio_url && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => playAudio(`${BACKEND_URL}${opt.continue_typing_audio_url}`)}
                                      >
                                        <Play className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="fallback-toggle"
                          checked={!!editingFieldInput.fallback_typing_system}
                          onChange={(e) => {
                            setEditingFieldInput({
                              ...editingFieldInput,
                              fallback_typing_system: e.target.checked ? 'yiddish_keypad' : null,
                              fallback_typing_prompt: e.target.checked ? editingFieldInput.fallback_typing_prompt : '',
                              fallback_typing_audio_url: e.target.checked ? editingFieldInput.fallback_typing_audio_url : '',
                            });
                          }}
                          className="rounded"
                        />
                        <Label htmlFor="fallback-toggle" className="cursor-pointer">
                          Add "type your own" as last option (option {(editingFieldInput.selection_options || []).length + 1})
                        </Label>
                      </div>
                      <p className="text-xs text-gray-500">
                        When enabled, option {(editingFieldInput.selection_options || []).length + 1} will let the caller type their own value
                        using a keypad if none of the above options match.
                      </p>

                      {editingFieldInput.fallback_typing_system && (
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Fallback Typing System</Label>
                            <Select
                              value={editingFieldInput.fallback_typing_system}
                              onValueChange={(value) =>
                                setEditingFieldInput({ ...editingFieldInput, fallback_typing_system: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TYPING_SYSTEMS.filter(ts => ts.value !== 'number').map((ts) => (
                                  <SelectItem key={ts.value} value={ts.value}>
                                    {ts.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">TTS Prompt (e.g. "Type your own using the keypad")</Label>
                            <Input
                              value={editingFieldInput.fallback_typing_prompt || ''}
                              onChange={(e) =>
                                setEditingFieldInput({ ...editingFieldInput, fallback_typing_prompt: e.target.value })
                              }
                              placeholder="e.g., Type your own using the keypad"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Audio Prompt (optional)</Label>
                            <div className="flex gap-2">
                              <Input
                                type="file"
                                accept="audio/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    try {
                                      const url = await handleAudioUpload(file);
                                      setEditingFieldInput({ ...editingFieldInput, fallback_typing_audio_url: url });
                                      toast.success('Fallback audio uploaded');
                                    } catch (err) { /* handled */ }
                                  }
                                }}
                              />
                              {editingFieldInput.fallback_typing_audio_url && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => playAudio(`${BACKEND_URL}${editingFieldInput.fallback_typing_audio_url}`)}
                                >
                                  <Play className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveFieldInput}>Save</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Menu Message Dialog */}
        <Dialog open={isMenuMessageDialogOpen} onOpenChange={setIsMenuMessageDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMenuMessage?.id ? 'Edit Menu Message' : 'Create Menu Message'}
              </DialogTitle>
            </DialogHeader>
            {editingMenuMessage && (
              <div className="space-y-4">
                <div>
                  <Label>Message Key</Label>
                  <Input
                    value={editingMenuMessage.message_key}
                    onChange={(e) =>
                      setEditingMenuMessage({ ...editingMenuMessage, message_key: e.target.value })
                    }
                    placeholder="e.g., main_menu, start_creating, catalog_prompt"
                    disabled={!!editingMenuMessage.id}
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier for this message</p>
                </div>
                <div>
                  <Label>Message Name</Label>
                  <Input
                    value={editingMenuMessage.message_name}
                    onChange={(e) =>
                      setEditingMenuMessage({ ...editingMenuMessage, message_name: e.target.value })
                    }
                    placeholder="e.g., Main Menu Message"
                  />
                </div>
                <div>
                  <Label>Audio File (Optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setMenuMessageAudioFile(file);
                        }
                      }}
                    />
                    {menuMessageAudioFile && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => playAudio(URL.createObjectURL(menuMessageAudioFile))}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Audio Link (Alternative to upload)</Label>
                  <Input
                    value={editingMenuMessage.audio_link || ''}
                    onChange={(e) =>
                      setEditingMenuMessage({ ...editingMenuMessage, audio_link: e.target.value })
                    }
                    placeholder="https://example.com/audio.mp3"
                  />
                </div>
                <div>
                  <Label>Text for TTS (Fallback if no audio)</Label>
                  <Textarea
                    value={editingMenuMessage.text || ''}
                    onChange={(e) =>
                      setEditingMenuMessage({ ...editingMenuMessage, text: e.target.value })
                    }
                    placeholder="e.g., Welcome to Azamein! To start creating..."
                    rows={4}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsMenuMessageDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        let audioUrl = editingMenuMessage.audio_url;
                        if (menuMessageAudioFile) {
                          audioUrl = await handleAudioUpload(menuMessageAudioFile);
                        }
                        const data = {
                          ...editingMenuMessage,
                          audio_url: audioUrl,
                        };
                        if (editingMenuMessage.id) {
                          await axios.put(`${API_URL}/admin/ivr/messages/${editingMenuMessage.id}`, data, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          toast.success('Menu message updated');
                        } else {
                          await axios.post(`${API_URL}/admin/ivr/messages`, data, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          toast.success('Menu message created');
                        }
                        setIsMenuMessageDialogOpen(false);
                        setEditingMenuMessage(null);
                        setMenuMessageAudioFile(null);
                        reloadMenuMessages();
                      } catch (error) {
                        toast.error(getErrorMessage(error, 'Failed to save menu message'));
                        console.error(error);
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Character Recording Dialog */}
        <Dialog open={isCharacterDialogOpen} onOpenChange={setIsCharacterDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCharacterRecording?.id ? 'Edit Character Recording' : 'Create Character Recording'}
              </DialogTitle>
            </DialogHeader>
            {editingCharacterRecording && (
              <div className="space-y-4">
                <div>
                  <Label>Character</Label>
                  <Input
                    value={editingCharacterRecording.character}
                    onChange={(e) =>
                      setEditingCharacterRecording({ ...editingCharacterRecording, character: e.target.value })
                    }
                    placeholder="e.g., A, B, 1, *, #"
                    disabled={!!editingCharacterRecording.id}
                    maxLength={1}
                  />
                  <p className="text-xs text-gray-500 mt-1">Single character (letter, number, or symbol)</p>
                </div>
                <div>
                  <Label>Audio File (Optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setCharacterAudioFile(file);
                        }
                      }}
                    />
                    {characterAudioFile && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => playAudio(URL.createObjectURL(characterAudioFile))}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Audio Link (Alternative to upload)</Label>
                  <Input
                    value={editingCharacterRecording.audio_link || ''}
                    onChange={(e) =>
                      setEditingCharacterRecording({ ...editingCharacterRecording, audio_link: e.target.value })
                    }
                    placeholder="https://example.com/audio.mp3"
                  />
                </div>
                <div>
                  <Label>Text for TTS (Fallback if no audio)</Label>
                  <Input
                    value={editingCharacterRecording.text || ''}
                    onChange={(e) =>
                      setEditingCharacterRecording({ ...editingCharacterRecording, text: e.target.value })
                    }
                    placeholder="e.g., A, B, One, Star"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsCharacterDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        let audioUrl = editingCharacterRecording.audio_url;
                        if (characterAudioFile) {
                          audioUrl = await handleAudioUpload(characterAudioFile);
                        }
                        const data = {
                          ...editingCharacterRecording,
                          audio_url: audioUrl,
                        };
                        if (editingCharacterRecording.id) {
                          await axios.put(`${API_URL}/admin/ivr/character-recordings/${editingCharacterRecording.id}`, data, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          toast.success('Character recording updated');
                        } else {
                          await axios.post(`${API_URL}/admin/ivr/character-recordings`, data, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          toast.success('Character recording created');
                        }
                        setIsCharacterDialogOpen(false);
                        setEditingCharacterRecording(null);
                        setCharacterAudioFile(null);
                        reloadCharacterRecordings();
                      } catch (error) {
                        toast.error(getErrorMessage(error, 'Failed to save character recording'));
                        console.error(error);
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
